import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { useEffect, useState } from 'react';
import { CommandsResponse, deleteSyncCommands, fetchSyncCommands } from '../../../../libraries/helpdesk/sync-commands';
import Header from '../../../components/util/header';
import { Table, Loader, Group } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import { formatSyncCommand } from '../../utils';
import SyncCommandModal from './sync-command-modal';
import CustomModal from '../../../components/custom-modal';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

const HelpDeskSyncCommands = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = new AbortController();

  const [list, setList] = useState<CommandsResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();
  const [selectedCommand, setSelectedCommand] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (filterBarData && limit) {
      loadSyncCommands();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadSyncCommands = () => {
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;

    fetchSyncCommands(
      controller,
      offset,
      limit,
      shouldFetchTotalRows,
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime ?? undefined),
      filterBarData?.entity || 0,
      filterBarData?.eid || 0
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const deleteCommand = (commandId: number) => {
    deleteSyncCommands(commandId)
      .then(() => {
        setSuccessMessage(`${lang.SYNC_COMMANDS_DELETE_SUCCESS}` || 'Sync command deleted');
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowSuccessDialog(false);
          loadSyncCommands();
        }, 2000);
      })
      .catch(err => {
        setErrorMessage(`${lang.SYNC_COMMANDS_DELETE_ERROR}` || 'Failed to delete sync command');
        setShowErrorDialog(true);
        setTimeout(() => {
          setShowErrorDialog(false);
        }, 2000);

        console.error(err);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const openModal = (command: any) => {
    setSelectedCommand(command);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedCommand(null);
    setShowModal(false);
  };

  return (
    <div className="sync-commands">
      <Header pageTitle={lang.GLOBAL_SYNC_COMMANDS} />

      <HelpdeskFilterBar
        onSearch={loadSyncCommands}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{ beginDateTime: true, endDateTime: true, eid: true, entity: true }}
      />

      <SyncCommandModal show={showModal} handleClose={closeModal} command={selectedCommand} />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_COMMAND_ID}</Table.Th>
              <Table.Th>Gid</Table.Th>
              <Table.Th>Eid</Table.Th>
              <Table.Th>{lang.GLOBAL_OPERATION}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_COMMAND}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.list.map((command, index) => (
                    <Table.Tr key={index} onClick={() => openModal(command)} style={{ cursor: 'pointer' }}>
                      <Table.Td>{command.commandId}</Table.Td>
                      <Table.Td>{command.gidKey}</Table.Td>
                      <Table.Td>{command.eidKey}</Table.Td>
                      <Table.Td>{command.operation}</Table.Td>
                      <Table.Td>{formatDateTimeNSecLabel(command.datetime)}</Table.Td>
                      <Table.Td>{formatSyncCommand(command.command, lang)}</Table.Td>
                      <Table.Td>
                        <Icon.IconX
                          stroke={2}
                          style={{ cursor: 'pointer' }}
                          onClick={e => {
                            e.stopPropagation();
                            deleteCommand(command.commandId);
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E33535"
        textColor="white"
      />
      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpDeskSyncCommands;
